import type { BiomassDataRow } from './productionUnitTable'
import type { FishStockLog, MortalityLog, WeightSampleLog } from '../types/production'
import { computeCurrentBiomassPerLot, type CurrentBiomassPerLotEntry } from './currentBiomassPerLot'

export type FeedWeeklyBiomassResolver = (args: {
  lotId?: string | null
  lotToken: string
  toRecordedAt?: string | null
}) => number | null

export const buildFeedWeeklyBiomassResolver = (args: {
  productionRows: readonly BiomassDataRow[]
  mortalityLogs: readonly MortalityLog[]
  weightSampleLogs: readonly WeightSampleLog[]
  fishStockLogs: readonly FishStockLog[]
  canonicalLotIdByToken: ReadonlyMap<string, string>
  /**
   * Fase 7 (docs/tier-a-merge-consumer-mapping.md §2.6 line 363): optional
   * parallel map carrying the lineage weight ([0, 1], first-weight-found
   * per Option A) associated with each `lotToken` resolved via
   * `canonicalLotIdByToken`. See `biomassAsOfResolver.ts` for the same
   * pattern and rationale (kept parallel, not merged into
   * `canonicalLotIdByToken`, so existing `Map<string, string>` callers
   * keep typechecking unchanged). Applied only on the token-fallback
   * path; an explicit `lotId` is an exact match and is never scaled.
   */
  canonicalWeightByToken?: ReadonlyMap<string, number>
}): FeedWeeklyBiomassResolver => {
  const cacheByDate = new Map<string, Map<string, CurrentBiomassPerLotEntry>>()

  const getBiomassByLotId = (toRecordedAt?: string): Map<string, CurrentBiomassPerLotEntry> => {
    const cacheKey = toRecordedAt?.trim() || '__latest__'
    const cached = cacheByDate.get(cacheKey)
    if (cached) return cached

    const derived = computeCurrentBiomassPerLot({
      productionRows: args.productionRows,
      mortalityLogs: args.mortalityLogs,
      weightSampleLogs: args.weightSampleLogs,
      fishStockLogs: args.fishStockLogs,
      toRecordedAt: toRecordedAt?.trim() || undefined
    })

    cacheByDate.set(cacheKey, derived)
    return derived
  }

  return ({ lotId, lotToken, toRecordedAt }) => {
    const explicitLotId = typeof lotId === 'string' ? lotId.trim() : ''
    const usedFallback = !explicitLotId
    const fallbackLotId = args.canonicalLotIdByToken.get(lotToken)?.trim() || ''
    const resolvedLotId = explicitLotId || fallbackLotId

    if (!resolvedLotId) return null

    const biomassByLotId = getBiomassByLotId(toRecordedAt?.trim() || undefined)
    const biomassKg = biomassByLotId.get(resolvedLotId)?.biomassKg ?? null
    if (biomassKg === null || !usedFallback) return biomassKg
    const weight = args.canonicalWeightByToken?.get(lotToken) ?? 1
    return weight === 1 ? biomassKg : biomassKg * weight
  }
}
